import { useMemo, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

interface UseQuestionnairesListParams {
    questionnaires: any[] | undefined;
    questionnaireResponses?: any[];
}

export function useQuestionnairesList({
    questionnaires,
    questionnaireResponses,
}: UseQuestionnairesListParams) {
    const { state } = useLocation();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [responseFilter, setResponseFilter] = useState<string>("");
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [loading, setLoading] = useState(false);
    const currentEncounterId = state?.encounterReference?.id

    const hasQuestionnaireResponses = useCallback((questionnaireId: string) => {
        if (!questionnaireResponses?.length) return false;

        const hasResponses = questionnaireResponses.some((response: any) => {
            const questionnaireRef = response.resource.questionnaire;

            const isSameQuestionnaire = questionnaireRef === `Questionnaire/${questionnaireId}` ||
                questionnaireRef === questionnaireId ||
                questionnaireRef?.includes(questionnaireId);

            return isSameQuestionnaire;
        });

        return hasResponses;
    }, [questionnaireResponses]);

    const hasResponseInEncounter = useCallback((questionnaireId: string) => {

        if (!questionnaireResponses?.length || !currentEncounterId) return false;

        return questionnaireResponses.some((questionnaireResponse: any) => {
            const questionnaireRef = questionnaireResponse.resource.questionnaire;
            const responseEncounterRef = questionnaireResponse.resource.encounter?.reference?.split("/").pop();
            return questionnaireRef === `Questionnaire/${questionnaireId}` && responseEncounterRef === currentEncounterId;
        });
    }, [questionnaireResponses, currentEncounterId]);

    const filteredQuestionnaires = useMemo(() => {
        if (!questionnaires?.length) return [] as any[];
        const term = searchTerm.toLowerCase();
        return questionnaires.filter((questionnaire: any) => {
            const matchesSearch = questionnaire.title?.toLowerCase().includes(term) ||
                questionnaire.description?.toLowerCase().includes(term) ||
                questionnaire.publisher?.toLowerCase().includes(term);

            if (!matchesSearch) return false;

            if (responseFilter === "responded") {
                return hasQuestionnaireResponses(questionnaire.id);
            } else if (responseFilter === "not-responded") {
                return !hasQuestionnaireResponses(questionnaire.id);
            }

            return true;
        });
    }, [questionnaires, searchTerm, responseFilter, hasQuestionnaireResponses]);

    return {
        filteredQuestionnaires,
        hasQuestionnaireResponses,
        searchTerm,
        setSearchTerm,
        selectedStatus,
        setSelectedStatus,
        responseFilter,
        setResponseFilter,
        anchorEl,
        setAnchorEl,
        loading,
        setLoading,
        hasResponseInEncounter,
    };
}


